import { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Modal, Text, useMantineTheme } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Calendar } from "iconsax-react";
import "dayjs/locale/pt-br";
import "dayjs/locale/es";
import moment from "moment";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { convertToDate, countdownDate } from "../../../utils/date";
import { useAuth } from "../../../context/auth";
import { IParamsPerformance } from "../../../hooks";

interface IProps {
  query: IParamsPerformance;
  setQuery: React.Dispatch<React.SetStateAction<IParamsPerformance>>;
  initialDate: string;
}

export default function ModalRangeDate({
  query,
  setQuery,
  initialDate,
}: IProps) {
  const theme = useMantineTheme();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const isLargerThan780 = useMediaQuery("(min-width: 780px)");
  const { user } = useAuth();
  const userCreatedAt = user?.created_at;

  const daysSinceProductCreated = useMemo(() => {
    if (!initialDate) return null;
    return countdownDate(new Date(initialDate));
  }, [initialDate]);

  const preDefinitionDates = useMemo(() => {
    const base = [
      {
        label: t("global.graphic_performance.filters.last_30_days"),
        value: 29,
      },
      {
        label: t("global.graphic_performance.filters.last_60_days"),
        value: 59,
      },
      {
        label: t("global.graphic_performance.filters.last_90_days"),
        value: 89,
      },
      {
        label: t("global.graphic_performance.filters.last_one_year"),
        value: 364,
      },
    ];
    if (daysSinceProductCreated !== null) {
      return [
        {
          label: t("global.graphic_performance.filters.period_all"),
          value: daysSinceProductCreated,
        },
        ...base,
      ];
    }
    return base;
  }, [daysSinceProductCreated, t]);

  const [opened, setOpened] = useState(false);
  const [tempDate, setTempDate] = useState<[Date | null, Date | null]>([
    query.close_date_init ?? null,
    query.close_date_end ?? null,
  ]);

  const [date, setDate] = useState<[Date | null, Date | null]>([
    query.close_date_init ?? null,
    query.close_date_end ?? null,
  ]);

  useEffect(() => {
    setTempDate([query.close_date_init ?? null, query.close_date_end ?? null]);
    setDate([query.close_date_init ?? null, query.close_date_end ?? null]);
  }, [query.close_date_init, query.close_date_end]);

  const endOfDay = (date: Date) => {
    return moment(date).endOf("day").toDate(); // 23:59:59.999
  };

  const handleSelectPreDefinition = (numberDays: number) => {
    const today = new Date();
    let startDate: Date;

    // Se for o período completo (mesmo valor que daysSinceProductCreated), usa a data de criação do serviço
    if (
      daysSinceProductCreated !== null &&
      numberDays === daysSinceProductCreated
    ) {
      startDate = moment(initialDate).startOf("day").toDate();
    } else {
      // Para os outros períodos, calcula baseado nos dias
      startDate = moment(today)
        .subtract(numberDays, "days")
        .startOf("day")
        .toDate();
    }

    const endDate = endOfDay(today);
    const newDateRange: [Date | null, Date | null] = [startDate, endDate];

    setDate(newDateRange);
    setTempDate(newDateRange);
    setQuery((prevQuery) => ({
      ...prevQuery,
      close_date_init: newDateRange[0] ?? prevQuery.close_date_init,
      close_date_end: endDate,
    }));

    setOpened(false);
  };

  const handleContinue = () => {
    const endDate = date[1]
      ? endOfDay(date[1])
      : endOfDay(date[0] ?? new Date());

    setTempDate(date);
    setQuery((prevQuery) => ({
      ...prevQuery,
      close_date_init: date[0] ?? prevQuery.close_date_init,
      close_date_end: endDate,
    }));
    setOpened(false);
  };

  const getButtonLabel = (date: [Date | null, Date | null]): string => {
    if (!date[0]) return t("operacoes.data_popover_info_user.select_dates");

    const startRaw = moment(date[0]);
    const endRaw = date[1] ? moment(date[1]) : null;
    const todayStart = moment().startOf("day");

    // Normaliza para limites do dia para evitar off-by-one com horas vindas do backend
    const startDay = startRaw.startOf("day");
    const endDay = endRaw ? endRaw.endOf("day") : null;

    const startDateFormated = convertToDate(date[0]);
    const endDateFormated = convertToDate(date[1] || undefined);

    if (!endDay)
      return startDay.isSame(todayStart, "day")
        ? t("operacoes.data_popover_info_user.today")
        : startDateFormated || "";

    const diffDays = endDay.diff(startDay, "days");

    if (endDay.isSame(todayStart, "day")) {
      const preDefinedLabel = preDefinitionDates.find(
        (item) => item.value === diffDays
      )?.label;
      if (preDefinedLabel) return preDefinedLabel;
      return t("operacoes.data_popover_info_user.from_to", {
        startDate: startDateFormated,
        endDate: endDateFormated,
      });
    }

    return t("operacoes.data_popover_info_user.from_to", {
      startDate: startDateFormated,
      endDate: endDateFormated,
    });
  };

  const effectiveDateRange: [Date | null, Date | null] = useMemo(() => {
    if (opened) return date;
    return [query.close_date_init ?? null, query.close_date_end ?? null];
  }, [opened, date, query.close_date_init, query.close_date_end]);

  const buttonLabel = getButtonLabel(effectiveDateRange);

  return (
    <>
      <Flex direction={"column"} gap={8} w="100%" mt={-8}>
        <Text fw={600} fz="14px" lh="155%">
          {t("global.graphic_performance.filters.period")}
        </Text>
        <Button
          variant="primaryOutline"
          miw={"100%"}
          color={theme.colors.basic[5]}
          leftIcon={<Calendar size="24" color={theme.colors.basic[5]} />}
          style={{
            width: "fit-content",
            display: "flex",
            gap: "12px",
            borderRadius: "8px",
            whiteSpace: "wrap",
            wordBreak: "break-word",
          }}
          styles={{
            root: {
              borderColor: theme.colors.basic[2],
              borderRadius: "8px",
              borderWidth: "1.5px",
              outline: `1.5px solid ${theme.colors.basic[2]}`,
              "&:hover": {
                backgroundColor: theme.colors.basic[0],
                color: theme.colors.other[2],
              },
              ":focus-visible": {
                outlineOffset: "0px !important",
                outline: `1.5px solid ${theme.colors.basic[2]}`,
              },
              ":focus": {
                outlineOffset: "0px !important",
                outline: `1.5px solid ${theme.colors.basic[2]}`,
              },
            },
            inner: {
              color: theme.colors.basic[5],
              fontFamily: "Montserrat",
              fontSize: "16px",
              lineHeight: "155%",
              fontWeight: 400,
            },
          }}
          onClick={() => {
            setOpened(!opened);
          }}
        >
          {buttonLabel}
        </Button>
      </Flex>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        size="auto"
        centered
        withCloseButton={false}
        styles={{
          content: {
            borderRadius: "12px",
          },
          body: {
            padding: "20px",
          },
        }}
      >
        <Flex
          direction={"column"}
          justify={"space-between"}
          w={{ base: "fit-content", 780: "100%" }}
        >
          <Flex
            direction={{ base: "column", 780: "row" }}
            w={{ base: "fit-content", 780: "100%" }}
            h={"315px"}
          >
            <DatePicker
              firstDayOfWeek={0}
              locale={language}
              mb={15}
              type="range"
              value={date}
              onChange={setDate}
              maxDate={new Date()}
              pr="20px"
              sx={{
                borderRight: isLargerThan780
                  ? `1.5px solid  ${theme.colors.basic[2]}`
                  : "none",
              }}
            />
            <Flex
              pl={20}
              direction={"column"}
              align="start"
              justify="start"
              h="auto"
              gap={8}
              w={{ base: "fit-content", 780: "100%" }}
            >
              <Text>
                {t("operacoes.data_popover_info_user.pre_definition")}
              </Text>
              <Box pl={10}>
                {preDefinitionDates.map((item, index) => (
                  <Text
                    key={`preDefinitionDates${index}`}
                    onClick={() => handleSelectPreDefinition(item.value)}
                    sx={{
                      cursor: "pointer",
                      transition: "0.5s text-decoration",
                      ":hover": {
                        cursor: "pointer",
                        textDecoration: "underline",
                        transition: "0.5s text-decoration",
                      },
                    }}
                  >
                    {item.label}
                  </Text>
                ))}
              </Box>
            </Flex>
          </Flex>

          <Flex
            pt={20}
            gap="16px"
            justify={"space-between"}
            align={"center"}
            sx={{ borderTop: `1.5px solid  ${theme.colors.basic[2]}` }}
            direction={{ base: "column", 780: "row" }}
          >
            <Button
              variant="primary"
              h={38}
              onClick={() => {
                setOpened(false);
                setDate(tempDate);
              }}
              w={{ base: "100%", 780: "auto" }}
            >
              {t("global.button.cancel")}
            </Button>
            <Flex gap={"5px"} align={"start"} direction={"column"}>
              <Text>{buttonLabel}</Text>
            </Flex>
            <Button
              variant="primary"
              h={38}
              onClick={handleContinue}
              w={{ base: "100%", 780: "auto" }}
            >
              {t("global.button.continue")}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
